import { LaunchStrategy, OpenContext } from '../types';
import { IS_WECHAT } from '../util/ua';
import { checkAppInstalledInWx } from '../webview/checkers';
import { LaunchApplicationOptions, launchByWxSDK } from '../webview/launcher';
import { AppChecker } from '../webview/AppChecker';

interface GetInstallStateOptions {
  /**
     * scheme，用于ios下检测App
     */
  packageUrl: string;
  /**
     * 包名，用于android下检测App
     */
  packageName: string;
}

export type WechatLaunchStrategyOptions = GetInstallStateOptions & Pick<LaunchApplicationOptions, 'appID'> & {
  /**
   * - ios中某些情况下可能会检测不到app已安装(请检查app的配置, 此方法仅作为备用)
   * - 使用此选项可以先调用微信的启动app方法，然后再根据结果来判断app是否安装
   * - 如果使用此方式，未安装app的情况下则会出现两次弹窗
   */
  skipCheck?: boolean;

  /**
   * 微信下载器的选项，不提供则会使用window.location.href的方式进行下载
   */
  downloader?: {
    /**
     * 获取渠道号的函数，提供给下载器使用，如果不填则默认使用context.model.confid的值
     * @param context - 拉起app时的上下文
     */
    getConfid?: (context: OpenContext) => string | number;
    /**
     * 开发者
     */
    developer: string;
    /**
     * 图标
     */
    icon: string;
    /**
     * 隐私政策链接
     */
    privacyAgreementUrl: string;
    /**
     * 权限详情链接
     */
    permissionUrl: string;
    /**
     * app的名称
     */
    name: string;
    /**
     * 获取app版本号的函数，可异步
     */
    getAppVersion: (context: OpenContext) => Promise<string> | string;
    /**
     * 获取文件的md5,用于微信下载器验证, 可异步
     */
    getMd5?: (context: OpenContext) => Promise<string> | string;
  }

};

/**
 * 创建在微信 webview内的拉起策略
 * @param options - 提供给微信jssdk或WeixinJSBridge的参数
 * @example
 * ```
 * new AppLauncher({
 *   customLaunch: [
 *     createWechatLaunchStrategy({
 *       packageName: 'com.tencent.qqlive',
 *       packageUrl: IS_IOS ? 'tenvideo2://' : 'txvideo://',
 *       downloader: {
 *       appid: '100730521',
 *       via: 'ANDROID.QQLIVE',
 *       getConfid: context => context.model.confid as string ?? '20449',
 *     },
 *   })]
 * })
 * ```
 */
export function createWechatLaunchStrategy(options: WechatLaunchStrategyOptions): LaunchStrategy {
  return async function wxLaunch(context, launcher, next) {
    if (IS_WECHAT) {
      const { logger } = context;
      logger.logKV('Environment', 'wechat');
      let isAppInstalled = false;
      if (!options.skipCheck)  {
        isAppInstalled = await checkAppInstalledInWx({
          packageName: options.packageName,
          packageUrl: options.packageUrl,
        });
      }
      logger.logKV('App Installed', isAppInstalled);

      let isSuccess = false;
      if (isAppInstalled || options.skipCheck) {
        logger.logKV('Launch Method', 'byWxSDK');
        logger.logKV('Launch URL', context.schemeUrl);
        if (options.appID) {
          isSuccess = await launchByWxSDK({
            appID: options.appID,
            extInfo: context.schemeUrl,
            parameter: context.schemeUrl,
          });
        } else {
          isSuccess = await launchByWxSDK({
            schemeUrl: context.schemeUrl,
          });
        }
        // 如果没有跳过检测，则可认为拉起成功
        if (!options.skipCheck) {
          isSuccess = true;
        }
      }
      // 启动失败了
      if (!isSuccess) {
        // 如果配置了downloader的选项
        if (options.downloader) {
          // 下载app
          const checker = new AppChecker({
            app_version: options.downloader.getAppVersion(context), // options.downloader.getAppVersion(context),
            confid: options.downloader.getConfid?.(context) ?? context.model.confid as string ?? '',
            developer: options.downloader.developer,
            icon: options.downloader.icon,
            md5: () => '',
            package: options.packageName || '',
            packageProtocol: options.packageUrl || '',
            name: options.downloader.name,
            permission_url: options.downloader.permissionUrl,
            playerDownloadCallback: '',
            privacy_agreement_url: options.downloader.privacyAgreementUrl,
            // 下面的是跟QQ相关的不用填写
            via: '',
            appid: '',
          });
          await checker.waitUntilReady();
          if (checker.downloader && context.isDownloadLink) {
            logger.log('use wx downloader');
            checker.downloader.start({
              confid: options.downloader.getConfid?.(context) ?? context.model.confid as string ?? '',
              url: context.fallbackUrl,
              // 传入md5进行校验
              md5Value: await options.downloader.getMd5?.(context),
              // 关闭cgi校验
              verify: false,
            });
          } else {
            // ios环境就跳转到下载链接
            launcher.launchByLocation(context.fallbackUrl, { isCheck: false });
          }
        } else {
          launcher.launchByLocation(context.fallbackUrl, { isCheck: false });
        }
      }
      return isSuccess;
    }
    return next();
  };
}
